import { FC, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

import PaperclipIcon from "../../../icons/PaperclipIcon";
import WarningAmberIcon from "../../../icons/WarningAmberIcon";

import { CONTACT_SUPPORT_ATTACHMENT_LIMITS } from "../../../../config/constants";
import ContactSupportFilesDisplay from "./ContactSupportFilesDisplay";

const { maxFiles, maxFileSizeMb, acceptLabel, allowedExtensions } =
  CONTACT_SUPPORT_ATTACHMENT_LIMITS;

interface ContactSupportDropzoneProps {
  fileCount: number;
  disabled: boolean;
  errorMessage: string | null;
  onFilesSelected: (files: File[]) => void;
}

const ContactSupportDropzone: FC<ContactSupportDropzoneProps> = ({
  fileCount,
  disabled,
  errorMessage,
  onFilesSelected,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const limitReached = fileCount >= maxFiles;
  const isInteractive = !disabled && !limitReached;
  const hasError = !!errorMessage && !limitReached;
  const isCompact = fileCount > 0;

  const openFilePicker = () => {
    if (!isInteractive) return;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { files } = event.target;
    if (files && files.length > 0) onFilesSelected(Array.from(files));
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!isInteractive) return;
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    if (!isInteractive) return;
    const { files } = event.dataTransfer;
    if (files && files.length > 0) onFilesSelected(Array.from(files));
  };

  const borderColor = limitReached
    ? "divider"
    : hasError
      ? "error.main"
      : isDragOver
        ? "primary.main"
        : "divider";

  const mutedColor = limitReached
    ? "text.disabled"
    : hasError
      ? "error.main"
      : "text.secondary";

  return (
    <Box>
      <Box
        onClick={openFilePicker}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed",
          borderColor,
          borderRadius: "8px",
          backgroundColor: limitReached ? "action.hover" : "background.paper",
          cursor: isInteractive ? "pointer" : "default",
          transition:
            "border-color 150ms ease-in-out, background-color 150ms ease-in-out",
          display: "flex",
          flexDirection: "column",
          gap: isCompact ? 1.5 : 1,
          px: isCompact ? 2 : 3,
          py: isCompact ? 1.5 : 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isCompact ? "row" : "column",
            alignItems: "center",
            justifyContent: isCompact ? "flex-start" : "center",
            gap: isCompact ? 1.5 : 1,
            textAlign: isCompact ? "left" : "center",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: isCompact ? 28 : 44,
              height: isCompact ? 28 : 44,
              borderRadius: isCompact ? "8px" : "12px",
              flexShrink: 0,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: limitReached
                ? "action.disabledBackground"
                : "background.paper",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
              color: hasError ? "error.main" : "text.primary",
            }}
          >
            {hasError ? (
              <WarningAmberIcon size={18} />
            ) : (
              <PaperclipIcon size={isCompact ? 16 : 20} />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: isCompact ? "flex-start" : "center",
              justifyContent: isCompact ? "space-between" : "center",
              flex: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: limitReached ? "text.disabled" : "text.primary",
                flexShrink: 0,
              }}
            >
              {limitReached
                ? "Attachment limit reached"
                : isCompact
                  ? "Drop more files or click to upload"
                  : "Drop files here or click to upload documents"}
            </Typography>

            <Typography
              variant="body2"
              noWrap={isCompact && !hasError}
              sx={{
                color: mutedColor,
                minWidth: 0,
                width: "100%",
                whiteSpace: hasError ? "normal" : undefined,
                wordBreak: hasError ? "break-word" : undefined,
                textOverflow: isCompact && !hasError ? "ellipsis" : "clip",
              }}
            >
              {limitReached
                ? `${fileCount} of ${maxFiles} files · Remove a file to add another`
                : hasError
                  ? errorMessage
                  : isCompact
                    ? `${fileCount} of ${maxFiles} files · ${acceptLabel} · ${maxFileSizeMb} MB each`
                    : `${acceptLabel} · up to ${maxFiles} files, ${maxFileSizeMb} MB each`}
            </Typography>
          </Box>
        </Box>

        <ContactSupportFilesDisplay disabled={disabled} />
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileInputChange}
        accept={allowedExtensions.join(",")}
      />
    </Box>
  );
};

export default ContactSupportDropzone;
